import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { InventoryItem, RoastedBulkGroup as RoastedBulkGroupType } from '../../types/inventory';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useRoastedBulkGroupsState } from '../../hooks/inventory-hooks/useRoastedBulkGroupsState';
import { RoastedBulkGroupsTable } from './RoastedBulkGroupsTable';
import { RoastedBulkGroupsMobile } from './RoastedBulkGroupsMobile';
import { RoastedBulkGroupAdjustModal } from './RoastedBulkGroupAdjustModal';
import { useLanguage } from '../../features/language/useLanguage';

interface RoastedBulkGroupsProps {
  nameFilter: string;
  statusFilter: 'all' | 'active' | 'inactive';
  stockStatusFilter: 'all' | 'available' | 'low' | 'depleted';
  currencySymbol: string;
  items: InventoryItem[];
  onEditItem?: (itemId: string) => void;
  onOpenHistory: (itemId: string) => void;
  onOpenRoastProfile: (context: { itemId: string; itemName: string; workOrderId?: string }) => void;
  canWrite?: boolean;
}

export const RoastedBulkGroups: React.FC<RoastedBulkGroupsProps> = ({
  nameFilter,
  statusFilter,
  stockStatusFilter,
  currencySymbol,
  items,
  onEditItem,
  onOpenHistory,
  onOpenRoastProfile,
  canWrite,
}) => {
  const { filteredGroups, expandedKeys, toggleGroup, isLoading, error } = useRoastedBulkGroupsState({
    nameFilter,
    statusFilter,
    stockStatusFilter,
    items,
  });

  const { t } = useLanguage();
  const queryClient = useQueryClient();
  // The modal lives here rather than in InventoryPage because this component
  // owns the groups query, so it also owns the refresh after a write-off.
  const [adjustGroup, setAdjustGroup] = useState<RoastedBulkGroupType | null>(null);

  if (isLoading) {
    return <LoadingSpinner text="Loading roasted bulk groups..." />;
  }

  if (error) {
    return (
      <div className="px-4 py-5 sm:p-6 text-center text-red-600">
        Failed to load roasted bulk groups.
      </div>
    );
  }

  if (filteredGroups.length === 0) {
    return (
      <div className="px-4 py-5 sm:p-6 text-center text-gray-500 italic">
        {t('inventory.roastedBulk.empty')}
      </div>
    );
  }

  return (
    <div
      className="bg-white"
      data-test-id="inventory-roasted-bulk-groups"
    >
      <RoastedBulkGroupsTable
        groups={filteredGroups}
        statusFilter={statusFilter}
        expandedKeys={expandedKeys}
        toggleGroup={toggleGroup}
        currencySymbol={currencySymbol}
        items={items}
        onEditItem={onEditItem}
        onOpenHistory={onOpenHistory}
        onOpenRoastProfile={onOpenRoastProfile}
        onAdjustStock={setAdjustGroup}
        canWrite={canWrite}
      />

      <RoastedBulkGroupsMobile
        groups={filteredGroups}
        statusFilter={statusFilter}
        expandedKeys={expandedKeys}
        toggleGroup={toggleGroup}
        currencySymbol={currencySymbol}
        items={items}
        onEditItem={onEditItem}
        onOpenHistory={onOpenHistory}
        onOpenRoastProfile={onOpenRoastProfile}
        onAdjustStock={setAdjustGroup}
        canWrite={canWrite}
      />

      <RoastedBulkGroupAdjustModal
        group={adjustGroup}
        isOpen={adjustGroup !== null}
        currencySymbol={currencySymbol}
        onClose={() => setAdjustGroup(null)}
        onAdjusted={() => {
          // Group totals and the expanded per-lot rows come from these queries,
          // so both must refetch or the table keeps showing pre-write-off
          // numbers until the next navigation.
          queryClient.invalidateQueries({ queryKey: ['inventory', 'roasted-bulk-groups'] });
          queryClient.invalidateQueries({ queryKey: ['inventory'] });
        }}
      />
    </div>
  );
};
